import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button, CircularProgress } from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { EmptyState } from './style/cards/_PdiHelpers';
import SftGlobalData from '../../library/SftGlobalData';

// ---------------------------------------------------------------------------
// Shared "this webshop can't be shown" surfaces for the webshop screens.
//
// Two scenarios the admin must make obvious instead of rendering an empty,
// editable-looking screen:
//   1. No webshop is linked to the account (no lic_clients_webshop row) →
//      `<NoWebshopState>`.
//   2. A webshop row exists but the mini-api call failed (wrong credentials,
//      not deployed, offline, inactive, …) → `<WebshopErrorState>`, which
//      maps the auth-proxy error code to a plain-language Dutch reason.
//
// Per the product decision these are MESSAGE-ONLY (no retry/connect buttons).
// ---------------------------------------------------------------------------

// Auth-proxy error codes (the `translation_key` on a {success:false} result)
// → [i18n key, Vlaamse fallback]. Keep the fallback usable on its own so the
// message reads correctly even before the locale files are regenerated.
const ERROR_COPY = {
    mini_api_unreachable:   ['cfg_webshop_err_unreachable',  'De webshop is niet bereikbaar. Controleer of de mini-api is uitgerold en of de URL en inloggegevens kloppen.'],
    webshop_not_active:     ['cfg_webshop_err_inactive',     'Deze webshop staat op niet-actief. Activeer ze om de instellingen te kunnen beheren.'],
    webshop_not_found:      ['cfg_webshop_err_notfound',     'Deze webshop werd niet gevonden in de licentiedatabank.'],
    webshop_not_configured: ['cfg_webshop_err_nourl',        'Voor deze webshop is nog geen mini-api-URL ingesteld.'],
    error_network:          ['cfg_webshop_err_network',      'Geen netwerkverbinding met de server. Probeer het later opnieuw.'],
    error_timeout:          ['cfg_webshop_err_timeout',      'De verbinding met de webshop verliep te traag (time-out).'],
    error_server:           ['cfg_webshop_err_server',       'De server gaf een onverwachte fout terug.'],
};

/**
 * Resolve a proxy error into a human reason. Accepts either a {message, code}
 * object, a raw {success:false, translation_key, message} proxy result, or a
 * plain string. Falls back to the raw message, then a generic line.
 */
export const webshopErrorText = (err, t) => {
    if (!err) return '';
    const code = typeof err === 'string' ? null : (err.code || err.translation_key);
    const entry = code ? ERROR_COPY[code] : null;
    if (entry) return t ? t(entry[0], entry[1]) : entry[1];
    // "Mini-api returned an error" on its own hides the one line that says
    // what went wrong: the proxy passes the shop's {code, message} body along
    // in data.body, so show that (plus the HTTP status) instead.
    if (code === 'mini_api_error' && err.data) {
        const upstream = err.data.body && typeof err.data.body === 'object' ? err.data.body.message : null;
        const status = err.data.status ? ` (HTTP ${err.data.status})` : '';
        if (upstream) {
            const lead = t ? t('cfg_webshop_err_miniapi', 'De webshop antwoordde met een fout:') : 'De webshop antwoordde met een fout:';
            return `${lead} ${upstream}${status}`;
        }
    }
    const msg = typeof err === 'string' ? err : err.message;
    return msg || (t ? t('cfg_webshop_err_generic', 'Er ging iets mis bij het laden van de webshop.') : 'Er ging iets mis bij het laden van de webshop.');
};

/**
 * Scenario 1 — no webshop linked to the account.
 *
 * Usually this is the sync cron not (yet) having populated
 * lic_clients_webshop. When `api` + `onProvisioned` are supplied, a button
 * runs the auth `webshop&action=provision` back-fill (scoped to the user's
 * client by the key) so they can link the shop themselves once the cron is
 * fixed, then re-fetch the list. Without those props it's a plain message.
 */
export const NoWebshopState = ({ t, api, onProvisioned, notify, theme }) => {
    const [busy, setBusy] = useState(false);
    const tr = (k, fb) => (t ? t(k, fb) : fb);
    const canProvision = !!api && typeof onProvisioned === 'function';

    const handleProvision = async () => {
        setBusy(true);
        try {
            const res = await SftGlobalData.post('webshop&action=provision', { key: api });
            const list = Array.isArray(res?.data) ? res.data : [];
            if (res?.success && list.length > 0) {
                if (notify) notify(tr('cfg_provision_ok', 'Webshop gekoppeld.'), 4000, 'top-right', theme);
                onProvisioned();
            } else if (res?.success) {
                if (notify) notify(tr('cfg_provision_empty', 'Geen webshop gevonden in de sync-gegevens. Controleer of de sync-cron correct draait en probeer opnieuw.'), 7000, 'top-right', theme);
            } else if (notify) {
                notify(res?.message || tr('cfg_provision_failed', 'Koppelen mislukt.'), 6000, 'top-right', theme);
            }
        } catch (e) {
            if (notify) notify(e?.message || tr('cfg_provision_failed', 'Koppelen mislukt.'), 6000, 'top-right', theme);
        } finally {
            setBusy(false);
        }
    };

    return (
        <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title={tr('cfg_no_webshop_title', 'Geen webshop gekoppeld')}
            description={tr('cfg_no_webshop_desc', 'Er is nog geen webshop aan dit account gekoppeld. Dit komt meestal doordat de sync-cron de gegevens nog niet heeft aangevuld. Los dat op en probeer daarna te synchroniseren.')}
            action={canProvision ? (
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleProvision}
                    disabled={busy}
                    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                >
                    {tr('cfg_provision_button', 'Webshop synchroniseren')}
                </Button>
            ) : undefined}
        />
    );
};
NoWebshopState.propTypes = {
    t: PropTypes.func,
    api: PropTypes.string,
    onProvisioned: PropTypes.func,
    notify: PropTypes.func,
    theme: PropTypes.string,
};

/**
 * Scenario 2 — webshop exists but the mini-api could not be reached. When `lang`
 * is supplied, offers a "Naar setup" button so the user isn't stuck on the
 * message (the setup wizard can install/update to make the shop reachable).
 */
export const WebshopErrorState = ({ error, t, lang }) => {
    const navigate = useNavigate();
    return (
        <EmptyState
            icon={<CloudOffOutlinedIcon />}
            title={t ? t('cfg_webshop_err_title', 'Webshop niet bereikbaar') : 'Webshop niet bereikbaar'}
            description={webshopErrorText(error, t)}
            action={lang ? (
                <Button variant="contained" size="small" onClick={() => navigate('/' + lang + '/webshop-setup')}>
                    {t ? t('cfg_webshop_err_to_setup', 'Naar setup') : 'Naar setup'}
                </Button>
            ) : undefined}
        />
    );
};
WebshopErrorState.propTypes = {
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    t: PropTypes.func,
    lang: PropTypes.string,
};
